import {
  APIProvider,
  ControlPosition,
  Map,
  useMap,
} from "@vis.gl/react-google-maps";
import PolylineWithCarMarkers from "./components/Polyline";
import { CustomRecenterControl } from "./components/CustomRecenterControl";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "hooks";
import { RootState } from "store";
import { useGetShareQuery } from "@services/share";
import { addCoordinate } from "store/slices";
import {
  CURRENT_LOCATION,
  DEFAULT_LAT,
  DEFAULT_LNG,
  DEFAULT_MAPID,
  DEFAULT_POLYLINEWEIGHT,
  DEFAULT_ZOOM_LEVEL,
  POLLING_INTERVAL,
  START_LOCATION,
} from "../../constants";
import { Coordinate } from "type";

const API_KEY =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY ??
  (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string);

const mapOptions = {
  mapId: DEFAULT_MAPID,
  defaultCenter: { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
  defaultZoom: DEFAULT_ZOOM_LEVEL,
  gestureHandling: "greedy",
  disableDefaultUI: true,
};

// Convert Coordinate[] to LatLngLiteral[]
function coordinatesToLatLng(
  coordinates: Coordinate[]
): google.maps.LatLngLiteral[] {
  return coordinates.map((coord) => ({
    lat: coord.latitude,
    lng: coord.longitude,
  }));
}

const MapContent = () => {
  const map = useMap();
  const dispatch = useAppDispatch();

  // Get shareId from Redux store
  const shareId = useAppSelector((state: RootState) => state?.share?.shareId);

  // Get coordinates from Redux store
  const coordinates = useAppSelector(
    (state: RootState) => state?.coordinates?.coordinates
  );

  // Convert coordinates to LatLngLiteral format and memoize
  const polylinePoints = useMemo(() => {
    return coordinatesToLatLng(coordinates);
  }, [coordinates]);

  // Real API call with polling
  const { data: apiShareData } = useGetShareQuery(shareId!, {
    skip: !shareId,
    pollingInterval: POLLING_INTERVAL,
  });

  // Process real API data
  useEffect(() => {
    if (apiShareData?.location) {
      const coordinate: Coordinate = {
        latitude: apiShareData.location.latitude.serviceValue,
        longitude: apiShareData.location.longitude.serviceValue,
        timestamp: new Date(
          apiShareData.location.latitude.time
        )?.toLocaleTimeString(),
      };

      // Only add if this is a new coordinate (check timestamp)
      const lastCoordinate = coordinates[coordinates.length - 1];
      if (
        !lastCoordinate ||
        lastCoordinate.timestamp !== coordinate.timestamp
      ) {
        dispatch(addCoordinate(coordinate));
      }
    }
  }, [apiShareData, dispatch, coordinates]);

  // Get current (last) location for map centering
  const currentLocation = useMemo(() => {
    if (coordinates.length > 0) {
      const lastCoordinate = coordinates[coordinates.length - 1];
      return {
        lat: lastCoordinate.latitude,
        lng: lastCoordinate.longitude,
      };
    }
    return { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
  }, [coordinates]);

  // Center map on current location when coordinates update
  useEffect(() => {
    if (map && coordinates.length > 0) {
      map.setCenter(currentLocation);
      map.setZoom(DEFAULT_ZOOM_LEVEL);
    }
  }, [map, currentLocation, coordinates.length]);

  return (
    <>
      <PolylineWithCarMarkers
        points={polylinePoints}
        polylineWeight={DEFAULT_POLYLINEWEIGHT}
        startMarkerTitle={START_LOCATION}
        endMarkerTitle={CURRENT_LOCATION}
      />
      <CustomRecenterControl
        controlPosition={ControlPosition.RIGHT_BOTTOM}
        endLocation={currentLocation}
        map={map}
      />
    </>
  );
};

const LiveLocationMap = () => {
  return (
    <APIProvider apiKey={API_KEY} libraries={["marker"]}>
      <Map {...mapOptions}>
        <MapContent />
      </Map>
    </APIProvider>
  );
};

export default LiveLocationMap;
